<template v-focustrap>
  <label :class="{ 'p-error': invalid, 'p-disabled': disabled }" v-if="!hideLabel">{{ label }}</label>
  <div class="field-wrapper">
    <InputText
      ref="inputEl"
      @blur="onBlur"
      :class="{ 'p-invalid': invalid }"
      :type="type"
      v-model.trim="value"
      :placeholder="placeholder"
      :disabled="disabled"
      :style="fullWidth ? 'width: 100%' : ''"
      :autocomplete="autocomplete"
      :autofocus="autofocus"
      :aria-label="label"
      @input="onInput"
      @keyup.enter="$emit('submit')"
    />
    <small v-if="allowTranslations" class="field-icon-right" v-tooltip.left="`${$t('Translations')}`">
      <a href="#" @click.stop.prevent="openTranslations()">
        <i class="fa fa-globe" aria-hidden="true"></i>
      </a>
    </small>
  </div>
  <template v-if="error && showErrors">
    <small v-if="typeof error === 'string'" class="p-error">{{ error }}</small>
    <template v-else-if="typeof error === 'object' && error.length">
      <template v-for="errorObj in error">
        <small class="p-error">{{ errorObj.$message }}</small>
      </template>
    </template>
  </template>

  <TranslationsPopup
    v-if="showTranslations"
    :field="field"
    @close="closeTranslations"
    @save="onTranslationsSave"
  />
</template>

<script>
import TranslationsPopup from "@/components/popups/TranslationsPopup.vue";
import {objectVariable} from "@/js/document/document";

export default {
  name: "TextInput",
  components: {TranslationsPopup},
  props: {
    modelValue: String,
    label: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    error: {
      default: null
    },
    hideLabel: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'text'
    },
    invalid: {
      type: Boolean,
      default: false
    },
    showErrors: {
      type: Boolean,
      default: true
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: String,
      default: 'on'
    },
    fullWidth: {
      type: Boolean,
      default: true
    },
    allowTranslations: {
      type: Boolean,
      default: false
    },
    translationsField: {
      type: String,
      default: null
    },
    translations: {
      type: Object,
      default: null
    }
  },

  emits: ['update:modelValue', 'blur', 'input'],

  mounted() {
    if (this.autofocus) this.$refs.inputEl.$el.focus();
  },

  computed: {
    value: {
      get() {
        return this.modelValue
      },
      set(value) {
        this.$emit('update:modelValue', value)
      }
    }
  },

  data() {
    return {
      showTranslations: false,
      field: null
    }
  },

  methods: {
    onBlur() {
      this.sanitize();
      this.$emit('blur');
    },

    onInput() {
      this.$emit('input');
    },

    sanitize() {
      this.value = this.$purify.sanitize(this.value);
    },

    openTranslations() {
      this.showTranslations = true;
      this.field = {
        label: this.label,
        value: this.value,
        type: 'TextInput',
        translations: {}
      };
      if (this.translations) {
        for (let key in this.translations) {
          this.field.translations[key] = objectVariable(this.translations[key], this.translationsField);
        }
      } else {
        this.field.translations[this.$root.$i18n.locale] = this.value
      }
    },

    closeTranslations() {
      this.showTranslations = false;
      this.field = null;
    },

    onTranslationsSave(translations) {
      this.$emit('translate', translations, this.translationsField);
      this.closeTranslations();
    }
  }
}
</script>

<style scoped>
.field-wrapper {
  position: relative;
  width: 100%;

  .field-icon-right {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1.4rem;

    a {
      color: var(--surface-900);
    }
  }
}
</style>
